import { DataType, Table, type Vector } from 'apache-arrow'
import type { OtuSearchResult } from '../../../Generated/OtuSearchResult.ts'
import type { GroupModeKey } from '../../../Generated/GroupMode.ts'
import type { Filter } from './OtuDataTableView.logic.js'

/*
 * The OTU table computed in the browser from the per-OTU cube (`GET /api/vis/otu/{scopeId}/cube`), with the
 * semantics of the backend's `OtuTableQuery`, `OtuSearchFilterBuilder` and `OtuSearchSortBuilder`: the same groups,
 * aggregates, filters and order as a server page. `OtuCubeTable.logic.test.ts` pins it to the values the server pages
 * for `OtuCubeIT`'s fixture; keep the two in step when the server's grouping changes.
 *
 * SPIN mode does no grouping: the server sends one summary row per SPIN (`GET …/spins`, the rows
 * `SpinOtuSearchService` pages, which `SpinSummaryIT` pins), and {@link spinSummaryGroups} lays them out as groups of
 * one so the same filters, sorts, pages, read columns and export run over them. The SPIN cases of the logic test
 * mirror that IT's fixture.
 */

export type OtuCubeGroupMode = GroupModeKey | 'SPIN'

export const LADDER_COLUMNS = [
  'tax_0_domain',
  'tax_1_kingdom',
  'tax_2_phylum',
  'tax_3_class',
  'tax_4_order',
  'tax_5_family',
  'tax_6_genus',
  'tax_7_species'
] as const

const LADDER_FIELDS = ['domain', 'kingdom', 'phylum', 'className', 'order', 'family', 'genus', 'species'] as const

const RANK_OF_MODE: Partial<Record<OtuCubeGroupMode, number>> = { FAMILY: 5, GENUS: 6, SPECIES: 7 }

// The database's en_US (glibc) collation ignores everything but letters and digits (spaces, punctuation, symbols such
// as `|`) until those compare equal. ICU's `ignorePunctuation` keeps symbols, so they are stripped instead.
const collator = new Intl.Collator('en-US')
const IGNORED = /[^\p{L}\p{N}\p{M}]/gu
const significant = (s: string): string => s.replace(IGNORED, '')

/** Strings equal but for ignored characters: the one whose first difference is one of those sorts first. */
function compareIgnored(a: string, b: string): number {
  const weights = (s: string) => Array.from(s, (ch) => (significant(ch) === '' ? ch.codePointAt(0)! : Infinity))
  const x = weights(a),
    y = weights(b)
  for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i]! < y[i]! ? -1 : 1
  return x.length - y.length
}

const compareKeyed = (a: string, keyA: string, b: string, keyB: string): number =>
  collator.compare(keyA, keyB) || compareIgnored(a, b) || (a < b ? -1 : a > b ? 1 : 0)

/**
 * Text order of the database's default collation (en_US): linguistic over letters and digits, then the ignored
 * characters, then by code point, as Postgres breaks ties of a deterministic collation.
 */
export const compareText = (a: string, b: string): number => compareKeyed(a, significant(a), b, significant(b))

/** A text column as per-row codes into [values] (-1 for null). [ranks] orders the codes by {@link compareText}. */
export type CodedColumn = { codes: Int32Array; values: string[]; ranks: () => Int32Array }

/** One entry per OTU of the scope; numbers are NaN where the cube has null. */
export type OtuCube = {
  size: number
  otuUuid: string[]
  clusterSize: Float64Array
  totalRawReads: Float64Array
  samplesPresent: Float64Array
  gradeBucket: Int32Array
  hitPercentage: Float64Array
  gradePercentage: Float64Array
  sparkTop: Float64Array
  sparkAlt: Float64Array
  processId: CodedColumn
  ladder: CodedColumn[]
  boldLink: CodedColumn
  binSpecies: CodedColumn
  /** -1 null, 0 false, 1 true. */
  binSharing: Int8Array
}

const lazyRanks = (values: string[]): (() => Int32Array) => {
  let ranks: Int32Array | null = null
  return () => {
    if (!ranks) {
      const keys = values.map(significant)
      const order = values.map((_, i) => i).sort((a, b) => compareKeyed(values[a]!, keys[a]!, values[b]!, keys[b]!))
      ranks = new Int32Array(values.length)
      order.forEach((code, rank) => (ranks![code] = rank))
    }
    return ranks
  }
}

const textAt = (vector: Vector, row: number): string | null => (vector.get(row) as string | null) ?? null

function codedColumn(vector: Vector | null, size: number): CodedColumn {
  const codes = new Int32Array(size).fill(-1)
  const values: string[] = []
  const index = new Map<string, number>()
  const intern = (value: string): number => {
    let code = index.get(value)
    if (code === undefined) {
      code = values.length
      values.push(value)
      index.set(value, code)
    }
    return code
  }
  if (vector && DataType.isDictionary(vector.type)) {
    // Decode each dictionary once rather than every row.
    let row = 0
    for (const data of vector.data) {
      const dictionary = data.dictionary!
      const remap = new Int32Array(dictionary.length)
      for (let i = 0; i < dictionary.length; i++) {
        const value = textAt(dictionary, i)
        remap[i] = value === null ? -1 : intern(value)
      }
      const indices = data.values as ArrayLike<number>
      for (let i = 0; i < data.length; i++, row++) {
        if (data.nullCount === 0 || data.getValid(i)) codes[row] = remap[indices[i]!]!
      }
    }
  } else if (vector) {
    for (let row = 0; row < size; row++) {
      const value = textAt(vector, row)
      if (value !== null) codes[row] = intern(value)
    }
  }
  return { codes, values, ranks: lazyRanks(values) }
}

function numbers(vector: Vector | null, size: number): Float64Array {
  const out = new Float64Array(size).fill(NaN)
  if (!vector) return out
  let row = 0
  for (const data of vector.data) {
    const values = data.values as ArrayLike<number | bigint>
    for (let i = 0; i < data.length; i++, row++) {
      if (data.nullCount === 0 || data.getValid(i)) out[row] = Number(values[i])
    }
  }
  return out
}

/** The columns of an OTU cube Arrow table, as the backend's `OtuCubeSql.CUBE_COLUMNS` names them. */
export function decodeOtuCube(table: Table): OtuCube {
  const size = table.numRows
  const child = (name: string): Vector | null => table.getChild(name)
  const uuids = child('otu_uuid')
  const sharing = child('bin_sharing')
  const binSharing = new Int8Array(size).fill(-1)
  if (sharing) {
    for (let i = 0; i < size; i++) {
      const value = sharing.get(i) as boolean | null
      if (value !== null) binSharing[i] = value ? 1 : 0
    }
  }
  return {
    size,
    otuUuid: Array.from({ length: size }, (_, i) => (uuids ? (textAt(uuids, i) ?? '') : '')),
    clusterSize: numbers(child('cluster_size'), size),
    totalRawReads: numbers(child('total_raw_reads'), size),
    samplesPresent: numbers(child('samples_present'), size),
    gradeBucket: Int32Array.from(numbers(child('grade_bucket'), size), (b) => (Number.isNaN(b) ? -1 : b)),
    hitPercentage: numbers(child('hit_percentage'), size),
    gradePercentage: numbers(child('grade_percentage'), size),
    sparkTop: numbers(child('spark_top'), size),
    sparkAlt: numbers(child('spark_alt'), size),
    processId: codedColumn(child('process_id'), size),
    ladder: LADDER_COLUMNS.map((name) => codedColumn(child(name), size)),
    boldLink: codedColumn(child('bold_link'), size),
    binSpecies: codedColumn(child('bin_species'), size),
    binSharing
  }
}

/** The SPIN summary of a scope: one entry per SPIN, its sums over its member OTUs and its own attributes. */
export type ScopeSpinSummary = {
  size: number
  /** Unique per row: entry i has code i. */
  spinId: CodedColumn
  memberCount: Float64Array
  clusterSize: Float64Array
  totalRawReads: Float64Array
  representation: Float64Array
  ladder: CodedColumn[]
  processId: CodedColumn
  hitPercentage: Float64Array
  gradePercentage: Float64Array
  sparkTop: Float64Array
  sparkAlt: Float64Array
  binSpecies: CodedColumn
  boldLink: CodedColumn
  /** -1 null, 0 false, 1 true. */
  binSharing: Int8Array
  evidenceEvaluatedAt: CodedColumn
}

function booleans(vector: Vector | null, size: number): Int8Array {
  const out = new Int8Array(size).fill(-1)
  if (!vector) return out
  for (let i = 0; i < size; i++) {
    const value = vector.get(i) as boolean | null
    if (value !== null) out[i] = value ? 1 : 0
  }
  return out
}

/** The columns of a SPIN summary Arrow table, as the backend's `SpinSummaryService.SUMMARY_COLUMNS` names them. */
export function decodeSpinSummary(table: Table): ScopeSpinSummary {
  const size = table.numRows
  const child = (name: string): Vector | null => table.getChild(name)
  return {
    size,
    spinId: codedColumn(child('spin_id'), size),
    memberCount: numbers(child('member_count'), size),
    clusterSize: numbers(child('cluster_size'), size),
    totalRawReads: numbers(child('total_raw_reads'), size),
    representation: numbers(child('representation'), size),
    ladder: LADDER_COLUMNS.map((name) => codedColumn(child(name), size)),
    processId: codedColumn(child('process_id'), size),
    hitPercentage: numbers(child('hit_percentage'), size),
    gradePercentage: numbers(child('grade_percentage'), size),
    sparkTop: numbers(child('spark_top'), size),
    sparkAlt: numbers(child('spark_alt'), size),
    binSpecies: codedColumn(child('bin_species'), size),
    boldLink: codedColumn(child('bold_link'), size),
    binSharing: booleans(child('bin_sharing'), size),
    evidenceEvaluatedAt: codedColumn(child('evidence_evaluated_at'), size)
  }
}

/** A cube without OTUs: what SPIN groups, which index the summary instead, pass where the shared code takes a cube. */
export const NO_OTUS: OtuCube = decodeOtuCube(new Table())

/** An ISO local date-time in UTC (`2026-10-01T12:30:00`, fractions optional) as epoch millis; NaN when unparseable. */
const utcMillis = (isoLocal: string): number => Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(isoLocal) ? isoLocal : `${isoLocal}Z`)

/**
 * Evidence measured after an explicit As of [atTime] ('' = Latest, which hides nothing): the backend nulls such
 * evidence but keeps its measurement time, so the table can say "measured later" instead of "never measured".
 */
export const measuredAfter = (evaluatedAt: string | null | undefined, atTime: string): boolean =>
  atTime !== '' && !!evaluatedAt && utcMillis(evaluatedAt) > utcMillis(atTime)

/**
 * The `/reads` matrix by the cube's rows: row r's samples (codes into [sampleIds]) with their reads are
 * `samples/reads[start[r] .. start[r + 1])`, zeros left out.
 */
export type OtuReads = {
  sampleCount: number
  sampleIds: string[]
  /** Reads per sample over the whole matrix, OTUs the cube lacks included. */
  sampleTotals: Float64Array
  start: Int32Array
  samples: Int32Array
  reads: Float64Array
}

/** Indexes the `(sample_id, otu_uuid, raw_reads)` matrix by the cube's rows. OTUs the cube lacks are skipped. */
export function indexOtuReads(table: Table, cube: OtuCube): OtuReads {
  const n = table.numRows
  const sampleIds = codedColumn(table.getChild('sample_id'), n)
  const otuIds = codedColumn(table.getChild('otu_uuid'), n)
  const reads = numbers(table.getChild('raw_reads'), n)
  const rowOfUuid = new Map<string, number>()
  cube.otuUuid.forEach((uuid, row) => rowOfUuid.set(uuid, row))
  const rowOfCode = Int32Array.from(otuIds.values, (uuid) => rowOfUuid.get(uuid) ?? -1)
  const sampleTotals = new Float64Array(sampleIds.values.length)

  const start = new Int32Array(cube.size + 1)
  for (let i = 0; i < n; i++) {
    const sample = sampleIds.codes[i]!
    if (sample < 0 || !(reads[i]! > 0)) continue
    sampleTotals[sample]! += reads[i]!
    const row = otuIds.codes[i]! < 0 ? -1 : rowOfCode[otuIds.codes[i]!]!
    if (row >= 0) start[row + 1]!++
  }
  for (let row = 0; row < cube.size; row++) start[row + 1]! += start[row]!
  const samples = new Int32Array(start[cube.size]!)
  const values = new Float64Array(start[cube.size]!)
  const fill = start.slice(0, cube.size)
  for (let i = 0; i < n; i++) {
    const row = otuIds.codes[i]! < 0 ? -1 : rowOfCode[otuIds.codes[i]!]!
    if (row >= 0 && reads[i]! > 0 && sampleIds.codes[i]! >= 0) {
      samples[fill[row]!] = sampleIds.codes[i]!
      values[fill[row]!++] = reads[i]!
    }
  }
  return { sampleCount: sampleIds.values.length, sampleIds: sampleIds.values, sampleTotals, start, samples, reads: values }
}

/**
 * The `/spins/reads` table by the summary's rows, in {@link OtuReads}' shape with summary rows for cube rows: each
 * SPIN's samples and reads, and per sample the reads of the whole scope from the rows without a `spin_id`. Rows of
 * SPINs the summary lacks are skipped.
 */
export function indexSpinReads(table: Table, summary: ScopeSpinSummary): OtuReads {
  const n = table.numRows
  const sampleIds = codedColumn(table.getChild('sample_id'), n)
  const spinIds = codedColumn(table.getChild('spin_id'), n)
  const reads = numbers(table.getChild('raw_reads'), n)
  const rowOfId = new Map<string, number>()
  summary.spinId.values.forEach((id, code) => rowOfId.set(id, code))
  const rowOfCode = Int32Array.from(spinIds.values, (id) => rowOfId.get(id) ?? -1)
  const sampleTotals = new Float64Array(sampleIds.values.length)
  const rowOf = (i: number): number => (spinIds.codes[i]! < 0 ? -1 : rowOfCode[spinIds.codes[i]!]!)

  const start = new Int32Array(summary.size + 1)
  for (let i = 0; i < n; i++) {
    const sample = sampleIds.codes[i]!
    if (sample < 0 || !(reads[i]! > 0)) continue
    if (spinIds.codes[i]! < 0) sampleTotals[sample]! += reads[i]!
    else if (rowOf(i) >= 0) start[rowOf(i) + 1]!++
  }
  for (let row = 0; row < summary.size; row++) start[row + 1]! += start[row]!
  const samples = new Int32Array(start[summary.size]!)
  const values = new Float64Array(start[summary.size]!)
  const fill = start.slice(0, summary.size)
  for (let i = 0; i < n; i++) {
    const row = rowOf(i)
    if (row >= 0 && reads[i]! > 0 && sampleIds.codes[i]! >= 0) {
      samples[fill[row]!] = sampleIds.codes[i]!
      values[fill[row]!++] = reads[i]!
    }
  }
  return { sampleCount: sampleIds.values.length, sampleIds: sampleIds.values, sampleTotals, start, samples, reads: values }
}

/** The table's groups over a cube, one entry per group; numbers are NaN where the server has null. */
export type OtuGroups = {
  mode: OtuCubeGroupMode
  size: number
  /** Group g's cube rows are `members[memberStart[g] .. memberStart[g + 1])`. */
  memberStart: Int32Array
  members: Int32Array
  /** The group key: a code of the key column (process id, BOLD link, rank), or for NONE the cube row. */
  key: Int32Array
  gradeBucket: Int32Array
  /** The group's `process_id` as filters and sorts see it: the key, MIN over the OTUs, or null (-1) for BIN. */
  processId: Int32Array
  /** The cube row with the smallest `otu_uuid`, which names a group that is not a taxon. */
  firstOtu: Int32Array
  clusterSize: Float64Array
  minClusterSize: Float64Array
  maxClusterSize: Float64Array
  avgClusterSize: Float64Array
  totalRawReads: Float64Array
  hitPercentage: Float64Array
  minHitPercentage: Float64Array
  maxHitPercentage: Float64Array
  gradePercentage: Float64Array
  minGradePercentage: Float64Array
  maxGradePercentage: Float64Array
  sparkTopAvg: Float64Array
  sparkAltAvg: Float64Array
  samplesPresent: Float64Array
  /** MIN code per rank, in {@link LADDER_COLUMNS} order. */
  ladder: Int32Array[]
  /** NaN until known: a group of several OTUs needs the reads matrix, see {@link withRepresentation}. */
  representation: Float64Array
  /** The OTUs of each group. */
  memberCount: Float64Array
  /** SPIN mode only: the summary each group g is row g of. */
  spin?: SpinGroups
}

/** SPIN groups are the summary's rows, in order: `ladder` and `processId` codes index its columns. */
export type SpinGroups = { summary: ScopeSpinSummary; atTime: string }

/** The ladder column [rank] of [groups]' codes: the summary's for SPIN, else the cube's. */
const ladderColumn = (groups: OtuGroups, cube: OtuCube, rank: number): CodedColumn =>
  (groups.spin ? groups.spin.summary.ladder : cube.ladder)[rank]!

const processIdColumn = (groups: OtuGroups, cube: OtuCube): CodedColumn => (groups.spin ? groups.spin.summary.processId : cube.processId)

// Grade buckets are whole percents (-1 without a hit), so a key code and a bucket pack into one number.
const BUCKET_SPAN = 4096
const packKey = (code: number, bucket: number): number => code * BUCKET_SPAN + bucket + BUCKET_SPAN / 2

/** Which cube rows form which group, mirroring `OtuTableQuery.rowConditions` and its `GROUP BY`. */
function assignGroups(cube: OtuCube, mode: GroupModeKey): { groupOf: Int32Array; key: Int32Array; size: number } {
  const groupOf = new Int32Array(cube.size).fill(-1)
  if (mode === 'NONE') {
    for (let row = 0; row < cube.size; row++) groupOf[row] = row
    return { groupOf, key: Int32Array.from(groupOf), size: cube.size }
  }
  const rank = RANK_OF_MODE[mode]
  const column = rank !== undefined ? cube.ladder[rank]! : mode === 'BIN' ? cube.boldLink : cube.processId
  // The pipeline writes a missing rank as NULL, '' or 'nan'; none of them names a taxon to group by.
  const excluded = new Uint8Array(column.values.length)
  if (rank !== undefined) column.values.forEach((value, code) => (excluded[code] = value === '' || value === 'nan' ? 1 : 0))

  const groupOfKey = new Map<number, number>()
  const keys: number[] = []
  for (let row = 0; row < cube.size; row++) {
    const code = column.codes[row]!
    if (code < 0 || excluded[code]) continue
    const packed = rank !== undefined ? code : packKey(code, cube.gradeBucket[row]!)
    let group = groupOfKey.get(packed)
    if (group === undefined) {
      group = keys.length
      keys.push(code)
      groupOfKey.set(packed, group)
    }
    groupOf[row] = group
  }
  return { groupOf, key: Int32Array.from(keys), size: keys.length }
}

const average = (sum: number, count: number): number => (count > 0 ? sum / count : NaN)

/** `ROUND(samples * 100.0 / scopeSamples, 2)` of Postgres numerics: half away from zero on the exact quotient. */
export const roundedPercent = (samples: number, scopeSamples: number): number =>
  Math.floor((samples * 20000 + scopeSamples) / (2 * scopeSamples)) / 100

/**
 * Groups the cube by [mode] as `OtuTableQuery` does: PROCESS_ID and BIN per key and grade bucket, a rank per taxon
 * (spanning grade buckets), NONE per OTU. MINs of text follow {@link compareText}. Representation is filled for
 * single-OTU groups when [scopeSamples] is known; {@link withRepresentation} adds the rest.
 */
export function groupOtuCube(cube: OtuCube, mode: GroupModeKey, scopeSamples: number | null): OtuGroups {
  const { groupOf, key, size } = assignGroups(cube, mode)
  const memberStart = new Int32Array(size + 1)
  for (let row = 0; row < cube.size; row++) if (groupOf[row]! >= 0) memberStart[groupOf[row]! + 1]!++
  for (let g = 0; g < size; g++) memberStart[g + 1]! += memberStart[g]!
  const members = new Int32Array(memberStart[size]!)
  const fill = memberStart.slice(0, size)
  for (let row = 0; row < cube.size; row++) if (groupOf[row]! >= 0) members[fill[groupOf[row]!]!++] = row

  const f64 = () => new Float64Array(size).fill(NaN)
  const groups: OtuGroups = {
    mode,
    size,
    memberStart,
    members,
    key,
    gradeBucket: new Int32Array(size),
    processId: new Int32Array(size).fill(-1),
    firstOtu: new Int32Array(size),
    clusterSize: f64(),
    minClusterSize: f64(),
    maxClusterSize: f64(),
    avgClusterSize: f64(),
    totalRawReads: f64(),
    hitPercentage: f64(),
    minHitPercentage: f64(),
    maxHitPercentage: f64(),
    gradePercentage: f64(),
    minGradePercentage: f64(),
    maxGradePercentage: f64(),
    sparkTopAvg: f64(),
    sparkAltAvg: f64(),
    samplesPresent: f64(),
    ladder: LADDER_COLUMNS.map(() => new Int32Array(size).fill(-1)),
    representation: f64(),
    memberCount: new Float64Array(size)
  }
  const processRanks = cube.processId.ranks()
  const ladderRanks = cube.ladder.map((column) => column.ranks())

  for (let g = 0; g < size; g++) {
    let clusterSum = 0,
      clusterMin = Infinity,
      clusterMax = -Infinity,
      clusterCount = 0,
      reads = 0,
      readsCount = 0,
      samplesMin = Infinity,
      bucketMin = Infinity,
      processMin = -1,
      first = -1
    const hit = { sum: 0, count: 0, min: Infinity, max: -Infinity }
    const grade = { sum: 0, count: 0, min: Infinity, max: -Infinity }
    const sparkTop = { sum: 0, count: 0 }
    const sparkAlt = { sum: 0, count: 0 }
    const ladderMin = LADDER_COLUMNS.map(() => -1)
    for (let m = memberStart[g]!; m < memberStart[g + 1]!; m++) {
      const row = members[m]!
      const cluster = cube.clusterSize[row]!
      if (!Number.isNaN(cluster)) {
        clusterSum += cluster
        clusterMin = Math.min(clusterMin, cluster)
        clusterMax = Math.max(clusterMax, cluster)
        clusterCount++
      }
      if (!Number.isNaN(cube.totalRawReads[row]!)) {
        reads += cube.totalRawReads[row]!
        readsCount++
      }
      if (!Number.isNaN(cube.samplesPresent[row]!)) samplesMin = Math.min(samplesMin, cube.samplesPresent[row]!)
      bucketMin = Math.min(bucketMin, cube.gradeBucket[row]!)
      for (const [acc, value] of [
        [hit, cube.hitPercentage[row]!],
        [grade, cube.gradePercentage[row]!]
      ] as const) {
        if (Number.isNaN(value)) continue
        acc.sum += value
        acc.count++
        acc.min = Math.min(acc.min, value)
        acc.max = Math.max(acc.max, value)
      }
      if (!Number.isNaN(cube.sparkTop[row]!)) {
        sparkTop.sum += cube.sparkTop[row]!
        sparkTop.count++
      }
      if (!Number.isNaN(cube.sparkAlt[row]!)) {
        sparkAlt.sum += cube.sparkAlt[row]!
        sparkAlt.count++
      }
      const process = cube.processId.codes[row]!
      if (process >= 0 && (processMin < 0 || processRanks[process]! < processRanks[processMin]!)) processMin = process
      for (let r = 0; r < LADDER_COLUMNS.length; r++) {
        const code = cube.ladder[r]!.codes[row]!
        if (code >= 0 && (ladderMin[r]! < 0 || ladderRanks[r]![code]! < ladderRanks[r]![ladderMin[r]!]!)) ladderMin[r] = code
      }
      if (first < 0 || cube.otuUuid[row]! < cube.otuUuid[first]!) first = row
    }
    groups.firstOtu[g] = first
    groups.gradeBucket[g] = bucketMin
    groups.processId[g] = mode === 'BIN' ? -1 : mode === 'PROCESS_ID' ? key[g]! : processMin
    if (clusterCount > 0) {
      groups.clusterSize[g] = clusterSum
      groups.minClusterSize[g] = clusterMin
      groups.maxClusterSize[g] = clusterMax
      groups.avgClusterSize[g] = clusterSum / clusterCount
    }
    if (readsCount > 0) groups.totalRawReads[g] = reads
    if (hit.count > 0) {
      groups.hitPercentage[g] = hit.sum / hit.count
      groups.minHitPercentage[g] = hit.min
      groups.maxHitPercentage[g] = hit.max
    }
    if (grade.count > 0) {
      groups.gradePercentage[g] = grade.sum / grade.count
      groups.minGradePercentage[g] = grade.min
      groups.maxGradePercentage[g] = grade.max
    }
    groups.sparkTopAvg[g] = average(sparkTop.sum, sparkTop.count)
    groups.sparkAltAvg[g] = average(sparkAlt.sum, sparkAlt.count)
    groups.samplesPresent[g] = samplesMin === Infinity ? NaN : samplesMin
    ladderMin.forEach((code, r) => (groups.ladder[r]![g] = code))
    groups.memberCount[g] = memberStart[g + 1]! - memberStart[g]!
    if (scopeSamples !== null && memberStart[g + 1]! - memberStart[g]! === 1 && samplesMin !== Infinity) {
      groups.representation[g] = roundedPercent(samplesMin, Math.max(scopeSamples, 1))
    }
  }
  return groups
}

/**
 * The SPIN summary as groups, group g being summary row g and its own single member, so read columns index it with
 * {@link indexSpinReads}. Counts, sums and representation are the server's; the spread columns stay null.
 */
export function spinSummaryGroups(summary: ScopeSpinSummary, atTime: string): OtuGroups {
  const size = summary.size
  const identity = Int32Array.from({ length: size }, (_, g) => g)
  const nulls = () => new Float64Array(size).fill(NaN)
  return {
    mode: 'SPIN',
    size,
    memberStart: Int32Array.from({ length: size + 1 }, (_, g) => g),
    members: identity,
    key: Int32Array.from(summary.spinId.codes),
    gradeBucket: new Int32Array(size),
    processId: Int32Array.from(summary.processId.codes),
    firstOtu: identity,
    clusterSize: summary.clusterSize,
    minClusterSize: nulls(),
    maxClusterSize: nulls(),
    avgClusterSize: nulls(),
    totalRawReads: summary.totalRawReads,
    hitPercentage: summary.hitPercentage,
    minHitPercentage: nulls(),
    maxHitPercentage: nulls(),
    gradePercentage: summary.gradePercentage,
    minGradePercentage: nulls(),
    maxGradePercentage: nulls(),
    sparkTopAvg: summary.sparkTop,
    sparkAltAvg: summary.sparkAlt,
    samplesPresent: nulls(),
    ladder: summary.ladder.map((column) => Int32Array.from(column.codes)),
    representation: summary.representation,
    memberCount: summary.memberCount,
    spin: { summary, atTime }
  }
}

/** Whether some group's representation still waits for the reads matrix. */
export const representationPending = (groups: OtuGroups): boolean => groups.representation.some((r) => Number.isNaN(r))

/**
 * [groups] with every group's representation: the distinct samples in which any of its OTUs has reads, as a percent
 * of the scope's samples. [scopeSamples] defaults to the samples the matrix names.
 */
export function withRepresentation(groups: OtuGroups, reads: OtuReads, scopeSamples: number | null): OtuGroups {
  const divisor = Math.max(scopeSamples ?? reads.sampleCount, 1)
  const representation = new Float64Array(groups.size)
  const seen = new Int32Array(reads.sampleCount).fill(-1)
  for (let g = 0; g < groups.size; g++) {
    let samples = 0
    for (let m = groups.memberStart[g]!; m < groups.memberStart[g + 1]!; m++) {
      const row = groups.members[m]!
      for (let s = reads.start[row]!; s < reads.start[row + 1]!; s++) {
        const sample = reads.samples[s]!
        if (seen[sample] !== g) {
          seen[sample] = g
          samples++
        }
      }
    }
    representation[g] = roundedPercent(samples, divisor)
  }
  return { ...groups, representation }
}

/**
 * Reads per group and sample: group g's samples (codes into `OtuReads.sampleIds`) and the reads of its OTUs summed
 * per sample are `samples/reads[start[g] .. start[g + 1])`.
 */
export type GroupSampleReads = { sampleCount: number; start: Int32Array; samples: Int32Array; reads: Float64Array }

/** Pivots the reads matrix onto [groups]: one pass over the members' reads. */
export function pivotGroupReads(groups: OtuGroups, otuReads: OtuReads): GroupSampleReads {
  const sum = new Float64Array(otuReads.sampleCount)
  const touched = new Int32Array(otuReads.sampleCount)
  let capacity = 0
  for (const row of groups.members) capacity += otuReads.start[row + 1]! - otuReads.start[row]!
  const start = new Int32Array(groups.size + 1)
  const samples = new Int32Array(capacity)
  const reads = new Float64Array(capacity)
  let n = 0
  for (let g = 0; g < groups.size; g++) {
    let t = 0
    for (let m = groups.memberStart[g]!; m < groups.memberStart[g + 1]!; m++) {
      const row = groups.members[m]!
      for (let e = otuReads.start[row]!; e < otuReads.start[row + 1]!; e++) {
        const sample = otuReads.samples[e]!
        // Zeros are not indexed, so a sample is new to the group exactly while its sum is 0.
        if (sum[sample] === 0) touched[t++] = sample
        sum[sample]! += otuReads.reads[e]!
      }
    }
    for (let i = 0; i < t; i++) {
      samples[n] = touched[i]!
      reads[n++] = sum[touched[i]!]!
      sum[touched[i]!] = 0
    }
    start[g + 1] = n
  }
  return { sampleCount: otuReads.sampleCount, start, samples: samples.slice(0, n), reads: reads.slice(0, n) }
}

/** The read columns of the table, each a set of samples: their codes and their reads over the whole scope. */
export type SampleColumns = { codes: Int32Array[]; totals: Float64Array }

/** [columns]' samples as codes of [otuReads]; a sample the matrix does not name has no reads. */
export function resolveSampleColumns(otuReads: OtuReads, columns: { sampleIds: string[] }[]): SampleColumns {
  const codeOf = new Map(otuReads.sampleIds.map((id, code) => [id, code]))
  const codes = columns.map((c) => Int32Array.from(c.sampleIds.flatMap((id) => codeOf.get(id) ?? [])))
  const totals = Float64Array.from(codes, (cs) => cs.reduce((sum, code) => sum + otuReads.sampleTotals[code]!, 0))
  return { codes, totals }
}

/** Reads of each group in one column. */
export function columnReads(pivot: GroupSampleReads, codes: Int32Array): Float64Array {
  const inColumn = new Uint8Array(pivot.sampleCount)
  for (const code of codes) inColumn[code] = 1
  const out = new Float64Array(pivot.start.length - 1)
  for (let g = 0; g < out.length; g++) {
    for (let e = pivot.start[g]!; e < pivot.start[g + 1]!; e++) if (inColumn[pivot.samples[e]!]) out[g]! += pivot.reads[e]!
  }
  return out
}

/** Reads of group g in every column, through one scratch row reused across calls. */
export function groupReadsReader(pivot: GroupSampleReads, columns: SampleColumns): (g: number) => Float64Array {
  const scratch = new Float64Array(pivot.sampleCount)
  return (g) => {
    for (let e = pivot.start[g]!; e < pivot.start[g + 1]!; e++) scratch[pivot.samples[e]!] = pivot.reads[e]!
    const out = new Float64Array(columns.codes.length)
    for (let c = 0; c < out.length; c++) {
      const codes = columns.codes[c]!
      for (let k = 0; k < codes.length; k++) out[c]! += scratch[codes[k]!]!
    }
    for (let e = pivot.start[g]!; e < pivot.start[g + 1]!; e++) scratch[pivot.samples[e]!] = 0
    return out
  }
}

/** [reads] as a percent of [total] to two decimals; NaN for a sample without reads. */
export const relativeAbundance = (reads: number, total: number): number => (total > 0 ? Math.round((reads / total) * 10000) / 100 : NaN)

/** The sort field of the read column of [key]. */
export const readsColumnField = (key: string): string => `__reads_${key}`

export const isReadsColumnField = (field: string): boolean => field.startsWith('__reads_')

type ColumnValue = { kind: 'number'; at: (g: number) => number } | { kind: 'text'; column: CodedColumn; code: (g: number) => number }

/** The group columns filters and sorts address, by UI field (and the filter builders' snake_case aliases). */
function columnOf(field: string, groups: OtuGroups, cube: OtuCube): ColumnValue | undefined {
  const number = (values: Float64Array): ColumnValue => ({ kind: 'number', at: (g) => values[g]! })
  const rank = LADDER_FIELDS.indexOf(field as (typeof LADDER_FIELDS)[number])
  if (rank >= 0) return { kind: 'text', column: ladderColumn(groups, cube, rank), code: (g) => groups.ladder[rank]![g]! }
  switch (field) {
    case 'processId':
      return { kind: 'text', column: processIdColumn(groups, cube), code: (g) => groups.processId[g]! }
    case 'memberCount':
    case 'member_count':
      return number(groups.memberCount)
    case 'clusterSize':
    case 'cluster_size':
      return number(groups.clusterSize)
    case 'minClusterSize':
    case 'min_cluster_size':
      return number(groups.minClusterSize)
    case 'maxClusterSize':
    case 'max_cluster_size':
      return number(groups.maxClusterSize)
    case 'avgClusterSize':
    case 'avg_cluster_size':
      return number(groups.avgClusterSize)
    case 'totalRawReads':
      return number(groups.totalRawReads)
    case 'hitPercentage':
      return number(groups.hitPercentage)
    case 'minHitPercentage':
      return number(groups.minHitPercentage)
    case 'maxHitPercentage':
      return number(groups.maxHitPercentage)
    case 'gradePercentage':
      return number(groups.gradePercentage)
    case 'minGradePercentage':
      return number(groups.minGradePercentage)
    case 'maxGradePercentage':
      return number(groups.maxGradePercentage)
    case 'representation':
      return number(groups.representation)
    case 'sparkTopAvg':
      return number(groups.sparkTopAvg)
    case 'sparkAltAvg':
      return number(groups.sparkAltAvg)
    default:
      return undefined
  }
}

/** Postgres `ILIKE` of [pattern]: `%` any run, `_` any one character, `\` escapes. */
export function ilikeRegExp(pattern: string): RegExp {
  let source = ''
  for (let i = 0; i < pattern.length; i++) {
    const c = pattern[i]!
    if (c === '\\' && i + 1 < pattern.length) source += escapeRegExp(pattern[++i]!)
    else if (c === '%') source += '.*'
    else if (c === '_') source += '.'
    else source += escapeRegExp(c)
  }
  return new RegExp(`^${source}$`, 'isu')
}

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')

/** The filter references the representation of every group. */
export const filtersNeedRepresentation = (filters: Filter[]): boolean => filters.some((f) => f.parameter === 'representation')

type Predicate = (g: number) => boolean

const NONE: Predicate = () => false

/**
 * The groups [filter] keeps. A filter on a column the cube lacks, with a type it has no comparison for, or with a
 * value that is no number on a numeric column keeps none: the chip stays visible, so the empty table says why.
 */
function predicateOf(filter: Filter, groups: OtuGroups, cube: OtuCube): Predicate {
  const value = String(filter.value ?? '')
  if (filter.parameter === 'anyTaxon') {
    const like = ilikeRegExp(`%${value}%`)
    const matches = LADDER_COLUMNS.map((_, r) => ladderColumn(groups, cube, r).values.map((v) => like.test(v)))
    return (g) => groups.ladder.some((codes, r) => codes[g]! >= 0 && matches[r]![codes[g]!]!)
  }
  const column = columnOf(filter.parameter, groups, cube)
  if (!column) return NONE
  const type = filter.filterType as string
  if (type === 'IS_NULL' || type === 'NOT_NULL') {
    const isNull: Predicate = column.kind === 'number' ? (g) => Number.isNaN(column.at(g)) : (g) => column.code(g) < 0
    return type === 'IS_NULL' ? isNull : (g) => !isNull(g)
  }
  if (column.kind === 'number') {
    const bound = Number(value)
    if (value.trim() === '' || Number.isNaN(bound)) return NONE
    const compare = numberComparison(type, bound)
    if (!compare) return NONE
    return (g) => {
      const v = column.at(g)
      return !Number.isNaN(v) && compare(v)
    }
  }
  const values = column.column.values
  let matches: (text: string) => boolean
  if (type === 'LIKE' || type === 'SUBSTR') {
    const like = ilikeRegExp(`%${value}%`)
    matches = (text) => like.test(text)
  } else {
    const compare = numberComparison(type, 0)
    if (!compare) return NONE
    matches = (text) => compare(compareText(text, value))
  }
  const memo = new Map<number, boolean>()
  return (g) => {
    const code = column.code(g)
    if (code < 0) return false
    let hit = memo.get(code)
    if (hit === undefined) {
      hit = matches(values[code]!)
      memo.set(code, hit)
    }
    return hit
  }
}

function numberComparison(type: string, bound: number): ((v: number) => boolean) | undefined {
  switch (type) {
    case 'EQUAL':
      return (v) => v === bound
    case 'LESS_THAN':
      return (v) => v < bound
    case 'GREATER_THAN':
      return (v) => v > bound
    case 'LESS_THAN_OR_EQUAL':
      return (v) => v <= bound
    case 'GREATER_THAN_OR_EQUAL':
      return (v) => v >= bound
    default:
      return undefined
  }
}

/** The groups passing every filter (the server's `WHERE` over `grouped`). */
export function filterGroups(groups: OtuGroups, cube: OtuCube, filters: Filter[]): Int32Array {
  const predicates: Predicate[] = []
  for (const filter of filters) {
    const predicate = predicateOf(filter, groups, cube)
    predicates.push(predicate)
  }
  const kept: number[] = []
  for (let g = 0; g < groups.size; g++) if (predicates.every((p) => p(g))) kept.push(g)
  return Int32Array.from(kept)
}

/** Whether a sort on [sortField] needs every group's representation. */
export const sortNeedsRepresentation = (sortField: string): boolean => sortField === 'representation'

/**
 * [indices] ordered as `OtuSearchSortBuilder` orders them: the sort column with Postgres' NULL placement (last
 * ascending, first descending), then `group_id` and `grade_bucket` ascending. A field the table has no column for
 * sorts by total reads descending, its default.
 */
export function sortGroups(
  groups: OtuGroups,
  cube: OtuCube,
  indices: Int32Array,
  sortField: string,
  sortDirection: 'ASC' | 'DESC'
): Int32Array {
  const known = columnOf(sortField, groups, cube)
  const column = known ?? columnOf('totalRawReads', groups, cube)!
  const direction = known ? sortDirection : 'DESC'

  const primary = new Float64Array(groups.size)
  if (column.kind === 'number') {
    for (let g = 0; g < groups.size; g++) primary[g] = column.at(g)
  } else {
    const ranks = column.column.ranks()
    for (let g = 0; g < groups.size; g++) primary[g] = column.code(g) < 0 ? NaN : ranks[column.code(g)]!
  }
  return sortGroupsBy(groups, cube, indices, primary, direction)
}

/** [indices] ordered by a value per group (NaN as null), with {@link sortGroups}' null placement and ties. */
export function sortGroupsBy(
  groups: OtuGroups,
  cube: OtuCube,
  indices: Int32Array,
  primary: Float64Array,
  direction: 'ASC' | 'DESC'
): Int32Array {
  const groupId = groupIdOrder(groups, cube)
  const sign = direction === 'ASC' ? 1 : -1
  const nullFirst = direction === 'DESC'
  return Int32Array.from(indices).sort((a, b) => {
    const x = primary[a]!,
      y = primary[b]!
    const xNull = Number.isNaN(x),
      yNull = Number.isNaN(y)
    if (xNull !== yNull) return xNull === nullFirst ? -1 : 1
    if (!xNull && x !== y) return (x < y ? -1 : 1) * sign
    return groupId[a]! - groupId[b]! || groups.gradeBucket[a]! - groups.gradeBucket[b]!
  })
}

/** Each group's position in `group_id` order: its key's text, for NONE its OTU's uuid, for SPIN its `spin_id`. */
function groupIdOrder(groups: OtuGroups, cube: OtuCube): Float64Array {
  const order = new Float64Array(groups.size)
  if (groups.spin) {
    const ranks = groups.spin.summary.spinId.ranks()
    for (let g = 0; g < groups.size; g++) order[g] = ranks[groups.key[g]!]!
    return order
  }
  if (groups.mode === 'NONE') {
    const byUuid = Array.from(groups.firstOtu).map((row, g) => [cube.otuUuid[row]!, g] as const)
    byUuid.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    byUuid.forEach(([, g], position) => (order[g] = position))
    return order
  }
  const rank = RANK_OF_MODE[groups.mode]
  const column = rank !== undefined ? cube.ladder[rank]! : groups.mode === 'BIN' ? cube.boldLink : cube.processId
  const ranks = column.ranks()
  for (let g = 0; g < groups.size; g++) order[g] = ranks[groups.key[g]!]!
  return order
}

/** A table row from the cube; `representationPending` while its representation waits for the reads matrix. */
export type OtuTableRow = OtuSearchResult & {
  sparkTopAvg?: number
  sparkAltAvg?: number
  representationPending?: boolean
  /** SPIN: the evidence was measured after the explicit As of, so its values are hidden rather than missing. */
  evidenceMeasuredLater?: boolean
  /** Reads per read column, in the order the table asked for them. */
  sampleReads?: Float64Array
}

const orUndefined = (value: number): number | undefined => (Number.isNaN(value) ? undefined : value)

const textOf = (column: CodedColumn, code: number): string | undefined => (code < 0 ? undefined : column.values[code])

/** `ARRAY_AGG(DISTINCT …)` of a text column over the group's OTUs: collation order, null last. */
function distinctTexts(groups: OtuGroups, g: number, column: CodedColumn, withNull: boolean): (string | null)[] {
  const codes = new Set<number>()
  for (let m = groups.memberStart[g]!; m < groups.memberStart[g + 1]!; m++) codes.add(column.codes[groups.members[m]!]!)
  const ranks = column.ranks()
  const present = [...codes].filter((c) => c >= 0).sort((a, b) => ranks[a]! - ranks[b]!)
  const texts: (string | null)[] = present.map((c) => column.values[c]!)
  return withNull && codes.has(-1) ? [...texts, null] : texts
}

/** The row id of group [g], as the server's `otuId`: the taxon of a rank group, the SPIN, else the group's first OTU. */
export function groupLabel(groups: OtuGroups, cube: OtuCube, g: number): string {
  if (groups.spin) return groups.spin.summary.spinId.values[groups.key[g]!]!
  const rank = RANK_OF_MODE[groups.mode]
  return rank !== undefined ? cube.ladder[rank]!.values[groups.key[g]!]! : cube.otuUuid[groups.firstOtu[g]!]!
}

/** The OTUs of group [g], most reads first, ties by `otu_uuid`. */
export function groupMembers(groups: OtuGroups, cube: OtuCube, g: number): string[] {
  const rows = Array.from(groups.members.subarray(groups.memberStart[g]!, groups.memberStart[g + 1]!))
  const reads = (row: number) => (Number.isNaN(cube.totalRawReads[row]!) ? -1 : cube.totalRawReads[row]!)
  rows.sort((a, b) => reads(b) - reads(a) || (cube.otuUuid[a]! < cube.otuUuid[b]! ? -1 : 1))
  return rows.map((row) => cube.otuUuid[row]!)
}

/** SPIN group [g] as `SpinOtuSearchService` pages it: a single-valued attribute is absent where the SPIN has none. */
function toSpinTableRow(groups: OtuGroups, spin: SpinGroups, g: number): OtuTableRow {
  const { summary, atTime } = spin
  const one = (column: CodedColumn): string[] | undefined => {
    const text = textOf(column, column.codes[g]!)
    return text === undefined ? undefined : [text]
  }
  const sharing = summary.binSharing[g]!
  const evaluatedAt = textOf(summary.evidenceEvaluatedAt, summary.evidenceEvaluatedAt.codes[g]!)
  const row: OtuTableRow = {
    otuId: summary.spinId.values[groups.key[g]!]!,
    memberCount: groups.memberCount[g]!,
    clusterSize: groups.clusterSize[g]!,
    fastaSequence: '',
    processId: one(summary.processId),
    hitPercentage: orUndefined(groups.hitPercentage[g]!),
    gradePercentage: orUndefined(groups.gradePercentage[g]!),
    totalRawReads: orUndefined(groups.totalRawReads[g]!),
    bins: one(summary.boldLink),
    binSpecies: one(summary.binSpecies),
    binSharing: sharing < 0 ? undefined : [sharing === 1],
    representation: orUndefined(groups.representation[g]!),
    sparkTopAvg: orUndefined(groups.sparkTopAvg[g]!),
    sparkAltAvg: orUndefined(groups.sparkAltAvg[g]!),
    evidenceEvaluatedAt: evaluatedAt
  }
  LADDER_FIELDS.forEach((field, r) => (row[field] = textOf(summary.ladder[r]!, groups.ladder[r]![g]!)))
  if (measuredAfter(evaluatedAt, atTime)) row.evidenceMeasuredLater = true
  if (Number.isNaN(groups.representation[g]!)) row.representationPending = true
  return row
}

/** Group [g] as the server's `OtuSearchResult` (sequence aside, which the cube does not carry). */
export function toTableRow(groups: OtuGroups, cube: OtuCube, g: number): OtuTableRow {
  if (groups.spin) return toSpinTableRow(groups, groups.spin, g)
  const rank = RANK_OF_MODE[groups.mode]
  const processIds =
    rank !== undefined
      ? (distinctTexts(groups, g, cube.processId, false) as string[])
      : groups.processId[g]! >= 0
        ? [cube.processId.values[groups.processId[g]!]!]
        : []
  const sharing = new Set<number>()
  for (let m = groups.memberStart[g]!; m < groups.memberStart[g + 1]!; m++) sharing.add(cube.binSharing[groups.members[m]!]!)
  const row: OtuTableRow = {
    otuId: groupLabel(groups, cube, g),
    clusterSize: groups.clusterSize[g]!,
    minClusterSize: orUndefined(groups.minClusterSize[g]!),
    maxClusterSize: orUndefined(groups.maxClusterSize[g]!),
    avgClusterSize: orUndefined(groups.avgClusterSize[g]!),
    fastaSequence: '',
    processId: processIds,
    hitPercentage: orUndefined(groups.hitPercentage[g]!),
    minHitPercentage: orUndefined(groups.minHitPercentage[g]!),
    maxHitPercentage: orUndefined(groups.maxHitPercentage[g]!),
    gradePercentage: orUndefined(groups.gradePercentage[g]!),
    minGradePercentage: orUndefined(groups.minGradePercentage[g]!),
    maxGradePercentage: orUndefined(groups.maxGradePercentage[g]!),
    totalRawReads: orUndefined(groups.totalRawReads[g]!),
    bins: distinctTexts(groups, g, cube.boldLink, true),
    binSpecies: distinctTexts(groups, g, cube.binSpecies, true),
    binSharing: [0, 1, -1].filter((v) => sharing.has(v)).map((v) => (v < 0 ? null : v === 1)),
    representation: orUndefined(groups.representation[g]!),
    sparkTopAvg: orUndefined(groups.sparkTopAvg[g]!),
    sparkAltAvg: orUndefined(groups.sparkAltAvg[g]!)
  }
  LADDER_FIELDS.forEach((field, r) => (row[field] = textOf(cube.ladder[r]!, groups.ladder[r]![g]!)))
  if (Number.isNaN(groups.representation[g]!)) row.representationPending = true
  return row
}
